import * as d3 from 'd3';

import { DoughnutChartData } from './DoughnutChart.model';
import { ItemFormat } from '../interfaces';
import { RoundingHelper } from '../../..';
import { highlightOnHover } from '../CategoricalChart/stroke-hover';
import { Tooltip } from '../CategoricalChart/tooltip';
import { whiteBackgroundColor } from '../../../../common/constants';

//TODO merge with labels.ts::30

const magicCoefficient = 4.2;
const margin = 6;

function drawLabel(databound: d3.selection.Update<ItemFormat>, ratio: number = 1) {
    ratio = ratio || 1;
    databound
        .transition()
        .attr('transform', (d, i) => {
            let x = d.x;
            let y = d.y;
            x = RoundingHelper.notNaN(x, 0);
            y = RoundingHelper.notNaN(y, 0);
            return `translate(${x},${y})`;
        });

    databound
        .select('rect')
        .attr('height', 15 * ratio)
        .attr('width', d => (d.item.formattedValue.length * magicCoefficient + margin) * ratio)
        .attr('transform', d => `translate(${-d.item.formattedValue.length * magicCoefficient * ratio / 2 - margin * ratio / 2},${-11 * ratio})`);

    databound
        .select('text')
        .style('fill', d => d.color || d.item.color)
        .text(d => d.item.formattedValue);
    return databound;
}

interface SliceFormat extends ItemFormat {
    path: string;
    centroid: [number, number];
    percentage: number;
}


export class DoughnutChartRenderer {
    private _arc: d3.svg.Arc<any>;
    private _layout: d3.layout.Pie<DoughnutChartData>;
    private _outerRadius: number;
    private _thickness: number;
    private _isLabelVisible: boolean;
    private _tooltip: Tooltip;

    constructor(private _element: d3.Selection<any>) {
        this._arc = d3.svg.arc();
        this._layout = d3.layout.pie<DoughnutChartData>()
            .value(d => d.value)
            .sort(null);
        this._element.append('g')
            .classed('doughnut', true)
            .append('g')
            .classed('slices', true);
        this._tooltip = new Tooltip(this._element.select('g.doughnut'));
        this.outerRadius(200)
            .thickness(.2)
            .showLabel(true);
    }

    showLabel(): boolean;
    showLabel(value: boolean): this;
    showLabel(value?: boolean): boolean | this {
        if (!arguments.length) {
            return this._isLabelVisible;
        }
        this._isLabelVisible = value;
        return this;
    }

    outerRadius(): number;
    outerRadius(value: number): this;
    outerRadius(value?: number): this | number {
        if (!arguments.length) {
            return this._outerRadius;
        }
        this._outerRadius = value;
        return this;
    }

    thickness(): number;
    thickness(value: number): this;
    thickness(value?: number): number | this {
        if (!arguments.length) {
            return this._thickness;
        }
        value = Math.min(value, 99.99);
        value = Math.max(10, value);
        this._thickness = value;
        return this;
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            return this;
        }
        return this._ratio;
    }

    render(data: DoughnutChartData[]): void {
        const ratio = this.ratio() || 1;
        const innerRadius = (1 - this._thickness / 100) * this._outerRadius;
        this._arc
            .outerRadius(this._outerRadius)
            .innerRadius(innerRadius);
        const formatted: SliceFormat[] = this._layout(data)
            .map(d => {
                const centroid = this._arc.centroid(d);
                const path = this._arc(d);
                const res: SliceFormat = {
                    path,
                    centroid,
                    color: d.data.color,
                    height: 15 * ratio,
                    item: d.data as any,
                    width: 0,
                    x: centroid[0],
                    y: centroid[1],
                    y1: 0,
                    y2: 0,
                    percentage: (d.endAngle - d.startAngle) / (2 * Math.PI)
                };
                return res;
            });

        const pie = this._element
            .select('g.doughnut')
            .attr('transform', `translate(${RoundingHelper.notNaN(this._outerRadius, 0)}, ${RoundingHelper.notNaN(this._outerRadius, 0)})`);

        // Render slices
        const dataBound = pie
            .select('g.slices')
            .selectAll('g.slice')
            .data(formatted);
        dataBound.exit()
            .remove();

        const enter = dataBound.enter();
        const sliceGroup = enter
            .append('g')
            .classed('slice', true);
        const sliceAppend = sliceGroup
            .append('path')
            .style('fill', whiteBackgroundColor);

        highlightOnHover(sliceAppend, {
            'mouseenter': (d, sel) => {
                const r = 0;
                let x = d.centroid[0];
                if (x > 0) {
                    x -= this._tooltip.width();
                }
                x += r;
                let y = d.centroid[1];
                if (y > 0) {
                    y -= this._tooltip.height();
                }
                y += r;
                const pos = { x, y };
                this._tooltip
                    .visible(true)
                    .position(pos)
                    .render(d);
            },
            'mouseout': (d, sel) => {
                this._tooltip.visible(false)
                    .render(d);
            }
        });

        dataBound
            .select('path')
            .attr('d', d => d.path)
            .transition()
            .style('fill', d => d.color);

        // Render labels
        const vl = enter
            .append('g')
            .classed('value-label', true);
        vl.append('rect');
        vl.append('text');
        const labelBound = pie
            .select('g.slices')
            .selectAll('g.value-label')
            .data(formatted);
        drawLabel(labelBound.classed('hidden-group', !this._isLabelVisible), ratio);
    }
}